import React, { useState } from 'react';
import { Heading } from '../../controls/Heading/Heading';
import { useLocale, useLocalization } from '../../context/Localization/localization';
import { ScrollableBody } from '../Layout/ScrollableBody';
import { CTAButton } from '../../controls/ButtonBase/index';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { GMUppercase } from '../../controls/Case';
import css from './OutdoorKiosk.module.scss';
import { useAppointmentState, useAppointmentUpdateAndPersist, useMaskEmail } from '../../context/Appointment/appointment';
import { sendMobileCheckInEmail } from '../../API/GoMoto';
import { maskValue } from '../../helpers/maskValue';
import { GMRoundedIcon, GMSizedIcon } from '../../controls/Icon';

export const OutdoorKiosk = (props: { onComplete: () => void; }) => {
    const Localization = useLocalization();
    const appointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const maskEmail = useMaskEmail();
    const locale = useLocale();
    const {
        dealer: {
            localization: { arrivalInstructions },
            key: dealerKey,
        },
    } = useKioskState();
    const [emailSent, setEmailSent] = useState<boolean>(false);
    const [markedAsComplete, setMarkedAsComplete] = useState<boolean>(false);

    React.useEffect(() => {
        if (markedAsComplete && !emailSent) {
            sendMobileCheckInEmail(appointment.key, dealerKey, locale, true, undefined, undefined, undefined, undefined, appointment.phoneTypeEnum)
                .then(
                    (_qrCode: string) => {
                        setEmailSent(true);
                    },
                );
        }
    }, [appointment.key, dealerKey, locale, emailSent, appointment.phoneTypeEnum, markedAsComplete]);

    React.useEffect(() => {
        /**
         * Mark the pre-check-in as complete so that the 'Im Here'
         * button will display only the desired screens
         *
         * An update here with allowEmptyingServiceLists=true may be needed to account for the case of the user emptying the services list
         */

        async function updateAppointment() {
            await setAppointment(
                {
                    ...appointment,
                    mobileCheckInComplete: true,
                },
                false,
                false,
                false,
                true,
                undefined,
                true,
                false,
                true,
                true,
            );
            setMarkedAsComplete(true);
        }

        if (!markedAsComplete) {
            updateAppointment();
        }
    }, [appointment, markedAsComplete, setAppointment]);

    return (
        <ScrollableBody
            heading={
                <Heading
                    roundedIcon={
                        <GMRoundedIcon
                            roundedBackgroundSize={'large'}
                            themeType={'secondary'}
                        >
                            <GMSizedIcon
                                iconType={'Complete'}
                                iconSize={'extraLarge'}
                                ariaLabel={Localization.CompleteCheckmarkIcon}
                            />
                        </GMRoundedIcon>
                    }
                    primaryHeading={Localization.PreCheckInComplete}
                />
            }
            proceedButtons={
                <CTAButton
                    styleType={'primary'}
                    onClick={props.onComplete}
                    label={Localization.ImHere}
                    kioskOptions={{
                        inlineOptions: {
                            sizedIcon: (
                                <GMSizedIcon
                                    iconSize={'custom'}
                                    iconType={'Complete'}
                                    ariaLabel={Localization.CompleteCheckmarkIcon}
                                />
                            ),
                        },
                    }}
                    disabled={!emailSent}
                    id={Localization.ImHere}
                    ariaLabel={Localization.ImHere}
                />
            }
            footing={
                <div className={css.disclaimer}>
                    {Localization.formatString(
                        Localization.PreCheckInDisclaimer,
                        maskEmail ? maskValue(appointment.email) : <a href={'mailto:' + appointment.email}>{appointment.email}</a>,
                    )}
                </div>
            }
        >
            {arrivalInstructions && <div className={css.arrivalInstructions}>{arrivalInstructions}</div>}

            <div className={css.whenYouArrive}>
                {Localization.formatString(
                    Localization.WhenYouArrive,
                    <b>
                        <GMUppercase>{Localization.ImHere}</GMUppercase>
                    </b>,
                )}
            </div>
        </ScrollableBody>
    );
};
